import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { useHierarchy } from '@/store/hierarchy';
import { useDestinations } from '@/store/destinations';
import isNil from 'lodash/isNil';

export const useVats = defineStore('vats', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.price_GetAllVats,
    dto: 'VatDto',
    signalR: 'VatUpdate',
  });

  /**
   * Gets VAT for a hierarchy node.
   * Traverses up the hierarchy tree to find VAT for the given season.
   * If season is specified but no VAT found, falls back to null-season.
   * Returns 0 if no VAT is configured.
   * @param hierarchyNodeId The hierarchy node ID
   * @param seasonId The season ID (undefined/null for no season)
   * @param destinationId The destination ID (undefined resolves to the brand's default destination)
   * @returns The VAT percentage, or 0 if not found
   */
  function getVat(hierarchyNodeId: number, seasonId?: number | null, destinationId?: number): number {
    const hierarchy = useHierarchy();
    const nodeAndAncestors = hierarchy.getNodeAndAncestors(hierarchyNodeId);

    // Normalize seasonId: treat null and undefined the same
    const normalizedSeasonId = seasonId ?? undefined;
    const resolvedDestinationId = destinationId ?? useDestinations().defaultDestination?.id;
    const destinationVats = globalStoreBase.loaded.value.filter((v) => v.destinationId === resolvedDestinationId);

    // First try with the specific season
    for (const node of nodeAndAncestors) {
      const vat = destinationVats.find((v) => {
        const vatSeasonId = v.seasonId ?? undefined;
        return v.hierarchyNodeId === node.id && vatSeasonId === normalizedSeasonId;
      });
      if (vat) {
        return vat.amount;
      }
    }

    // If season was specified but no VAT found, try with null-season
    if (!isNil(normalizedSeasonId)) {
      for (const node of nodeAndAncestors) {
        const vat = destinationVats.find((v) => v.hierarchyNodeId === node.id && isNil(v.seasonId));
        if (vat) {
          return vat.amount;
        }
      }
    }

    return 0;
  }

  return { ...globalStoreBase, getVat };
});
